// components/FloatingAIBuilder.tsx
'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Sparkles } from 'lucide-react';

interface Props {
    onOpen: () => void;
}

export default function FloatingAIBuilder({ onOpen }: Props) {
    const btnRef = useRef<HTMLButtonElement>(null);
    const glowRef = useRef<HTMLSpanElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        // Floating bob animation
        gsap.to(btnRef.current, {
            y: -6,
            duration: 1.8,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
        });

        // Glow pulse
        gsap.to(glowRef.current, {
            scale: 1.6,
            opacity: 0,
            duration: 1.4,
            repeat: -1,
            ease: 'power2.out',
        });
    }, []);

    const handleHover = () => {
        gsap.to(labelRef.current, {
            opacity: 1,
            x: 0,
            duration: 0.3,
            ease: 'power2.out',
        });
    };

    const handleLeave = () => {
        gsap.to(labelRef.current, {
            opacity: 0,
            x: 12,
            duration: 0.2,
            ease: 'power2.in',
        });
    };

    const handleClick = () => {
        gsap.to(btnRef.current, {
            scale: 0.88,
            duration: 0.12,
            yoyo: true,
            repeat: 1,
            ease: 'power2.inOut',
            onComplete: onOpen,
        });
    };

    return (
        <div className="fixed right-5 bottom-8 z-50 flex items-center gap-3">
            {/* Tooltip Label */}
            <span
                ref={labelRef}
                style={{ opacity: 0, transform: 'translateX(12px)' }}
                className="bg-gray-900 text-white text-xs font-semibold
                   px-3 py-2 rounded-xl shadow-xl whitespace-nowrap
                   pointer-events-none select-none"
            >
                AI Product Builder ✨
            </span>

            {/* Button */}
            <div className="relative">
                <span
                    ref={glowRef}
                    className="absolute inset-0 rounded-full
                     bg-purple-500 opacity-40"
                />
                <button
                    ref={btnRef}
                    onClick={handleClick}
                    onMouseEnter={handleHover}
                    onMouseLeave={handleLeave}
                    aria-label="Open AI Product Builder"
                    className="relative w-16 h-16 rounded-full
                     bg-gradient-to-br from-violet-500 via-purple-600
                     to-indigo-600 shadow-2xl shadow-purple-500/40
                     flex items-center justify-center
                     ring-4 ring-purple-400/30
                     hover:ring-purple-400/60 transition-shadow"
                >
                    <Sparkles className="w-7 h-7 text-white drop-shadow" />
                </button>
            </div>
        </div>
    );
}